import React, { ComponentProps, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, StyleSheet, TouchableOpacity } from 'react-native';

import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import { PopupIcon } from '@components/Web/PopupIcon';
import {
  ProjectMemberRole,
  TaskMemberRole,
  TeamMemberRole,
  useCreateShareLinkMutation,
  useGetTaskQuery,
} from '@graphql/generated';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { useRenameDocument } from '@hooks/useRenameDocument';
import { useShareImagesLink } from '@hooks/useShareImagesLink.web';
import theme from '@themes/theme';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  copyTextToClipboard,
} from '@utils/copyTextToClipboard';
import { getDocumentIds } from '@utils/getDocumentIds';

import { buildWebRenameOnSelect } from './renameMenuHelper';

export const MediaShareModal: React.FC<{
  isGlobalSearch?: boolean;
  fromChatMessage?: boolean;
  fromChatId?: string;
  onDeleteFile?: () => void;
}> = ({ isGlobalSearch = false, onDeleteFile }) => {
  const { t } = useTranslation(['models', 'shared']);
  const { activeIndex, previewDocuments, setPreviewDocuments } =
    usePreviewDocuments();
  const taskDocs = previewDocuments?.[activeIndex]?.taskDocuments || [];
  const projectDocs = previewDocuments?.[activeIndex]?.projectDocuments || [];
  const [showCopied, setShowCopied] = useState(false);
  // This component is reachable both through ImagesPreview.web.tsx's
  // <WebModal> (which, like MediaViewer.web.tsx, wraps an RN <Modal> that
  // portals to document.body on web) and, directly, as a react-navigation
  // `modalStyle` screen (app-stack.tsx's 'media-share-modal' route) whose
  // cardStyleInterpolator applies a `translateY` transform — which, same as
  // the Modal portal, becomes the containing block for any `position:
  // fixed` toast nested inside it. BottomToast portals straight to
  // document.body instead, sidestepping both (TA-10845; see
  // BottomToast.web.tsx's own comment for the full explanation).
  const copyFailedToastRef = useRef<BottomToastHandle>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameExtension, setRenameExtension] = useState('');
  const [renameError, setRenameError] = useState<string | undefined>();
  const { renameDocument, loading: renameLoading } = useRenameDocument();
  const [createShareLink] = useCreateShareLinkMutation();
  const { onShareImages } = useShareImagesLink();

  const taskId =
    !isGlobalSearch && taskDocs.length > 0 ? taskDocs[0].task?.id : '';
  const { data: taskData } = useGetTaskQuery({
    variables: {
      id: taskId ?? '',
    },
    skip: !taskId,
  });

  const projectUUId =
    !isGlobalSearch && projectDocs.length > 0
      ? projectDocs[0].project?.uuid
      : '';
  const { project } = useProjectFromQuery({
    projectUUId: projectUUId ?? '',
  });

  let role = '';
  let teamRole = '';
  let projectRole = '';
  if (!isGlobalSearch && taskDocs.length > 0) {
    const { getTask: task } = taskData || {};
    role = task?.currentUserRole || '';
    projectRole = task?.currentUserProjectRole || '';
    teamRole = task?.currentUserProjectTeamRole || '';
  } else if (!isGlobalSearch && projectDocs.length > 0) {
    role = project?.currentUserRole || '';
    teamRole = project?.currentUserTeamRole || '';
  }

  const showCopyFailedToast = (
    reason: keyof typeof CLIPBOARD_FAILURE_MESSAGE_KEYS
  ) => {
    copyFailedToastRef.current?.show(
      t(CLIPBOARD_FAILURE_MESSAGE_KEYS[reason]),
      { duration: 3000, icon: 'warning' }
    );
  };

  const copyToClipboard = async (link: string) => {
    if (!link) {
      showCopyFailedToast('link-generation-failed');
      return;
    }
    const result = await copyTextToClipboard(link);
    if (result.success) {
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 3000);
    } else {
      showCopyFailedToast(result.reason);
    }
  };

  const onShare = async () => {
    try {
      const doc = previewDocuments?.[activeIndex];
      if (!doc) return;

      const { file } = doc;
      if (!file) return;

      const fileUrl = file.url;
      if (fileUrl?.startsWith('file:') || fileUrl?.startsWith('data:')) {
        await copyToClipboard(fileUrl);
        return;
      }

      // Only images/videos are supported by the logged-out /images-preview/
      // page; other file types get a createShareLink URL, matching Project
      // Files & Media sharing (TA-10090).
      if (doc.isImage || doc.isVideo) {
        const url = await onShareImages([doc]);
        if (url) {
          await copyToClipboard(url);
          return;
        }
      }

      const result = await createShareLink({
        variables: { documentIds: getDocumentIds([doc]) },
      });
      const sharedUrl = result.data?.createShareLink?.sharedUrl;
      await copyToClipboard(sharedUrl ?? '');
    } catch (error) {
      console.error('Failed to copy URL to clipboard:', error);
      showCopyFailedToast('unknown');
    }
  };

  let menuList: ComponentProps<typeof PopupIcon>['popupProps']['menuList'];
  if (
    (role !== ProjectMemberRole.Viewer && role !== TaskMemberRole.Viewer) ||
    teamRole === TeamMemberRole.Owner ||
    teamRole === TeamMemberRole.Admin ||
    teamRole === TeamMemberRole.Member ||
    projectRole !== ProjectMemberRole.Viewer
  ) {
    menuList = [
      {
        name: t('projects.files.rename'),
        iconName: 'Edit',
        onSelect: buildWebRenameOnSelect(previewDocuments?.[activeIndex], {
          setRenameValue,
          setRenameExtension,
          setRenameError,
          setShowRenameModal,
        }),
      },
      {
        name: t('projects.files.share'),
        iconName: 'Share2',
        testID: 'share-button',
        onSelect: () => onShare(),
      },
      {
        name: t('projects.files.forward'),
        iconName: 'Forward',
        isComingSoon: true,
        onSelect: () => {},
      },
      {
        name: t('projects.files.info'),
        iconName: 'Info',
        isComingSoon: true,
        onSelect: () => {},
      },
      ...(teamRole === TeamMemberRole.Member
        ? []
        : [
            {
              name: t('projects.files.delete'),
              iconName: 'Trash2',
              isRed: true,
              onSelect: onDeleteFile,
            },
          ]),
    ];
  } else {
    menuList = [
      {
        name: t('projects.files.rename'),
        iconName: 'Edit',
        onSelect: buildWebRenameOnSelect(previewDocuments?.[activeIndex], {
          setRenameValue,
          setRenameExtension,
          setRenameError,
          setShowRenameModal,
        }),
      },
      {
        name: t('projects.files.forward'),
        iconName: 'Forward',
        isComingSoon: true,
        onSelect: () => {},
      },
      {
        name: t('projects.files.info'),
        iconName: 'Info',
        isComingSoon: true,
        onSelect: () => {},
      },
    ];
  }

  const handleRename = () => {
    const trimmedBase = renameValue.trim();
    if (!trimmedBase) {
      setRenameError(t('projects.files.renameError'));
      return;
    }
    const doc = previewDocuments?.[activeIndex];
    if (!doc) return;

    const fullName = trimmedBase + renameExtension;
    if (fullName === doc.name) {
      setShowRenameModal(false);
      return;
    }

    renameDocument(
      doc.id,
      fullName,
      (newName: string) => {
        setPreviewDocuments((prev) =>
          prev?.map((d, i) => (i === activeIndex ? { ...d, name: newName } : d))
        );
        setShowRenameModal(false);
      },
      (error: Error) => setRenameError(error.message)
    );
  };

  return (
    <>
      <BottomToast ref={copyFailedToastRef} />
      {showCopied && (
        <WebModal
          accessibilityLabel='Copied'
          children={
            <Text variant='labelSmall' color='textPrimary' m='m'>
              Link Generated Successfully and Copied to Clipboard
            </Text>
          }
          onClose={() => setShowCopied(false)}
          visible={showCopied}></WebModal>
      )}
      <WebModal
        accessibilityLabel='Rename File'
        visible={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title={t('projects.files.rename')}
        width={400}>
        <Box padding='m'>
          <Box flexDirection='row' alignItems='center'>
            <Box flex={1}>
              <TextInput
                style={renameStyles.input}
                value={renameValue}
                onChangeText={(text) => {
                  setRenameValue(text);
                  if (renameError) setRenameError(undefined);
                }}
                autoFocus
                selectTextOnFocus
                placeholder={t('projects.files.renamePlaceholder')}
                placeholderTextColor={theme.colors.grey04}
              />
            </Box>
            {renameExtension !== '' && (
              <Text variant='body' color='grey04' marginLeft='xs'>
                {renameExtension}
              </Text>
            )}
          </Box>
          {renameError && (
            <Text variant='metadata' color='alertRed' marginTop='xs'>
              {renameError}
            </Text>
          )}
          <Box flexDirection='row' justifyContent='flex-end' marginTop='m'>
            <TouchableOpacity
              onPress={() => setShowRenameModal(false)}
              style={renameStyles.cancelButton}>
              <Text variant='labelSmall' color='textPrimary'>
                {t('shared:cancel')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleRename}
              disabled={renameLoading || !renameValue.trim()}
              style={[
                renameStyles.saveButton,
                (renameLoading || !renameValue.trim()) &&
                  renameStyles.disabledButton,
              ]}>
              <Text variant='labelSmall' color='white'>
                {t('shared:save')}
              </Text>
            </TouchableOpacity>
          </Box>
        </Box>
      </WebModal>
      <PopupIcon
        testID='more-button'
        name='MoreVertical'
        color='white'
        isWhiteIcon={true}
        popupProps={{
          position: 'left',
          offset: [20, theme.spacing.xxxs],
          menuList,
        }}
      />
    </>
  );
};

const renameStyles = StyleSheet.create({
  input: {
    borderWidth: 1,
    borderColor: theme.colors.grey03,
    borderRadius: theme.borderRadii.xs,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  cancelButton: {
    paddingVertical: theme.spacing.s,
    paddingHorizontal: theme.spacing.s,
    marginRight: theme.spacing.s,
  },
  saveButton: {
    paddingVertical: theme.spacing.s,
    paddingHorizontal: theme.spacing.s,
    backgroundColor: theme.colors.black,
    borderRadius: theme.borderRadii.xs,
  },
  disabledButton: {
    opacity: 0.5,
  },
});
